import React, { useCallback, useEffect } from 'react';
import { useTranslation } from '../../../context/LanguageContext';
import { formatCurrency } from '../../../utils/formatters';
// import qz from 'qz-tray'; // Now imported in printFunctions.js
import { useQZTray } from '../../../context/QZTrayContext';
import createCustomerReceipt from './receipt-templates/CustomerReceipt';
import { fetchOrderItems } from './utils/fetchOrderItems';
import { createReceiptHTML } from './utils/createReceiptHTML';
import { createReturnReceiptHTML } from './utils/createReturnReceiptHTML';
import { printReceipt, openCashDrawer } from './utils/printFunctions';

const ReceiptPrinter = React.forwardRef(({ settings }, ref) => {
  const { t, language } = useTranslation();
  const { isConnected, isConnecting, connectQZ, printers } = useQZTray();

  const handleConnect = useCallback(async () => {
    try {
      await connectQZ();
    } catch (err) {
      console.error('Failed to connect to QZ Tray');
    }
  }, [connectQZ]);

  useEffect(() => {
    if (!isConnected && !isConnecting) {
      handleConnect();
    }
  }, [handleConnect, isConnected, isConnecting]);

  const listPrinters = useCallback(() => {
    return printers;
  }, [printers]);

  const processPrint = useCallback(async (order) => {
    
    if (!settings || !order) {
      return { success: false, error: 'Missing settings or order data' };
    }
    
    // Only fetch order items if they are missing or empty
    // This preserves the correct items that were already fetched and displayed
    let orderToProcess;
    if (!order.items || order.items.length === 0) {
      orderToProcess = await fetchOrderItems(order);
    } else {
      orderToProcess = order;
    }
    
    if (!isConnected) {
      try {
        await handleConnect();
      } catch (connectionError) {
        console.error('Failed to connect to QZ-Tray:', connectionError);
        return { success: false, error: 'Failed to connect to QZ-Tray' };
      }
      
      // If we're still not connected after trying, abort
      if (!isConnected) {
        console.error('Still not connected to QZ-Tray after connection attempt');
        return { success: false, error: 'Could not establish connection to QZ-Tray' };
      }
    }

    try {
      const { printing } = settings;
      let selectedPrinter = printing?.printer_name;

      if (!selectedPrinter) {
        if (printers && printers.length > 0) {
          selectedPrinter = printers[0];
        } else {
          return;
        }
      }

      // Clean up printer name and ensure it's valid
      selectedPrinter = selectedPrinter.trim();
      
      // Find the matching printer object
      let matchingPrinter = printers.find(printer => 
        printer.value === selectedPrinter || printer.label === selectedPrinter
      );
      
      if (!matchingPrinter) {
        if (printers && printers.length > 0) {
          matchingPrinter = printers[0];
          printing.printer_name = printers[0].value || printers[0].label;
        } else {
          return;
        }
      }
      
      // Use the printer's value property for QZ Tray
      selectedPrinter = matchingPrinter.value;
      
      // Get paper dimensions
      const paperWidth = printing.paper_size || '80mm';
      const paperHeight = printing.paper_height || 'auto';
      const pageWidth = parseInt(paperWidth);
      const pageHeight = parseInt(paperHeight.replace('mm', ''));
      
      // Create receipt HTML content - use return template if isReturn is set
      const receiptContent = orderToProcess.isReturn 
        ? createReturnReceiptHTML(orderToProcess, settings, t, language, formatCurrency)
        : createReceiptHTML(orderToProcess, settings, t, language, formatCurrency);
        
      // Create print data for both receipts
      const printData = [
        // First print the main receipt
        { 
          type: 'html',
          format: 'plain',
          data: receiptContent,
          options: {
            pageWidth: pageWidth + 'mm',
            pageHeight: pageHeight + 'mm',
            css: true
          }
        },
        // Then print the customer receipt
        {
          type: 'html',
          format: 'plain',
          data: createCustomerReceipt(orderToProcess, settings, t),
          options: {
            pageWidth: '58mm',
            pageHeight: 'auto',
            css: true
          }
        }
      ];

      // Open cash drawer if enabled
      if (printing.open_drawer_after_print) {
        await openCashDrawer(selectedPrinter);
      }

      // Print receipt
      const dimensions = { pageWidth, pageHeight };
      
      try {
        // Here's the actual printing command
        await printReceipt(selectedPrinter, printData, dimensions);
        return { success: true, printer: selectedPrinter };
      } catch (printError) {
        console.error('Error during printReceipt call:', printError);
        return { success: false, error: printError.message || 'Print failed' };
      }

    } catch (error) {
      console.error('Error preparing receipt:', error);
      return { success: false, error: error.message || 'Error preparing receipt' };
    }
  }, [t, language, settings, isConnected, handleConnect, printers]);

  React.useImperativeHandle(ref, () => ({
    printReceipt: processPrint,
    connectQZ: handleConnect,
    listPrinters
  }));

  return null;
});

export default ReceiptPrinter;